// Sistema de permisos y roles

export const PERMISSIONS = {
  // Permisos básicos CRUD
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  
  // Permisos específicos del sistema
  MANAGE_USERS: 'manage_users',
  MANAGE_COMPANIES: 'manage_companies',
  MANAGE_EMPLOYEES: 'manage_employees',
  MANAGE_PAYROLLS: 'manage_payrolls',
  MANAGE_SALARIES: 'manage_salaries',
  VIEW_REPORTS: 'view_reports',
  EXPORT_DATA: 'export_data',
  IMPORT_DATA: 'import_data',
  MANAGE_SETTINGS: 'manage_settings',
  VIEW_AUDIT_LOG: 'view_audit_log',
  APPROVE_PAYROLLS: 'approve_payrolls',
  PROCESS_PAYMENTS: 'process_payments'
};

export const ROLES = {
  SUPER_ADMIN: {
    name: 'super_admin',
    displayName: 'Super Administrador',
    description: 'Acceso completo al sistema',
    permissions: [
      PERMISSIONS.CREATE,
      PERMISSIONS.READ,
      PERMISSIONS.UPDATE,
      PERMISSIONS.DELETE,
      PERMISSIONS.MANAGE_USERS,
      PERMISSIONS.MANAGE_COMPANIES,
      PERMISSIONS.MANAGE_EMPLOYEES,
      PERMISSIONS.MANAGE_PAYROLLS,
      PERMISSIONS.MANAGE_SALARIES,
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.EXPORT_DATA,
      PERMISSIONS.IMPORT_DATA,
      PERMISSIONS.MANAGE_SETTINGS,
      PERMISSIONS.VIEW_AUDIT_LOG,
      PERMISSIONS.APPROVE_PAYROLLS,
      PERMISSIONS.PROCESS_PAYMENTS
    ],
    color: 'red'
  },
  
  ADMIN: {
    name: 'admin',
    displayName: 'Administrador',
    description: 'Administrador general de la empresa',
    permissions: [
      PERMISSIONS.CREATE,
      PERMISSIONS.READ,
      PERMISSIONS.UPDATE,
      PERMISSIONS.DELETE,
      PERMISSIONS.MANAGE_EMPLOYEES,
      PERMISSIONS.MANAGE_PAYROLLS,
      PERMISSIONS.MANAGE_SALARIES,
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.EXPORT_DATA,
      PERMISSIONS.APPROVE_PAYROLLS,
      PERMISSIONS.PROCESS_PAYMENTS
    ],
    color: 'orange'
  },

  HR_MANAGER: {
    name: 'hr_manager',
    displayName: 'Gerente RRHH',
    description: 'Gestión de recursos humanos y empleados',
    permissions: [
      PERMISSIONS.CREATE,
      PERMISSIONS.READ,
      PERMISSIONS.UPDATE,
      PERMISSIONS.MANAGE_COMPANIES,
      PERMISSIONS.MANAGE_EMPLOYEES,
      PERMISSIONS.MANAGE_PAYROLLS,
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.EXPORT_DATA
    ],
    color: 'blue'
  },

  HR_ASSISTANT: {
    name: 'hr_assistant',
    displayName: 'Asistente RRHH',
    description: 'Asistente de recursos humanos',
    permissions: [
      PERMISSIONS.CREATE,
      PERMISSIONS.READ,
      PERMISSIONS.UPDATE,
      PERMISSIONS.MANAGE_EMPLOYEES,
      PERMISSIONS.VIEW_REPORTS
    ],
    color: 'cyan'
  },

  ACCOUNTANT: {
    name: 'accountant',
    displayName: 'Contador',
    description: 'Gestión contable y reportes financieros',
    permissions: [
      PERMISSIONS.READ,
      PERMISSIONS.UPDATE,
      PERMISSIONS.MANAGE_SALARIES,
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.EXPORT_DATA,
      PERMISSIONS.APPROVE_PAYROLLS
    ],
    color: 'green'
  },

  PAYROLL_SPECIALIST: {
    name: 'payroll_specialist',
    displayName: 'Especialista en Nóminas',
    description: 'Especialista en procesamiento de nóminas',
    permissions: [
      PERMISSIONS.READ,
      PERMISSIONS.UPDATE,
      PERMISSIONS.MANAGE_PAYROLLS,
      PERMISSIONS.MANAGE_SALARIES,
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.PROCESS_PAYMENTS
    ],
    color: 'purple'
  },

  SUPERVISOR: {
    name: 'supervisor',
    displayName: 'Supervisor',
    description: 'Supervisor de área',
    permissions: [
      PERMISSIONS.READ,
      PERMISSIONS.UPDATE,
      PERMISSIONS.VIEW_REPORTS
    ],
    color: 'indigo'
  },

  USER: {
    name: 'user',
    displayName: 'Usuario',
    description: 'Acceso básico de consulta',
    permissions: [
      PERMISSIONS.READ
    ],
    color: 'gray'
  }
};

// Función para verificar si un usuario tiene un permiso específico
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  
  const role = Object.values(ROLES).find(r => r.name === user.role);
  if (!role) return false;
  
  return role.permissions.includes(permission);
};

// Función para verificar si un usuario tiene cualquiera de los permisos especificados
export const hasAnyPermission = (user, permissions) => {
  return permissions.some(permission => hasPermission(user, permission));
};

// Función para verificar si un usuario tiene todos los permisos especificados
export const hasAllPermissions = (user, permissions) => {
  return permissions.every(permission => hasPermission(user, permission));
};

// Función para obtener información del rol
export const getRoleInfo = (roleName) => {
  return Object.values(ROLES).find(role => role.name === roleName) || null;
};

// Función para obtener los permisos de un usuario
export const getUserPermissions = (user) => {
  if (!user || !user.role) return [];
  
  const role = getRoleInfo(user.role);
  return role ? role.permissions : [];
};

// Función para verificar si un rol es admin
export const isAdmin = (user) => {
  return hasAnyPermission(user, [PERMISSIONS.MANAGE_USERS, PERMISSIONS.MANAGE_COMPANIES]);
};

// Función para verificar si un rol es super admin
export const isSuperAdmin = (user) => {
  return user?.role === 'super_admin';
};

// Función para filtrar elementos del menú basado en permisos
export const filterMenuByPermissions = (menuItems, user) => {
  return menuItems.filter(item => {
    if (item.permissions) {
      return hasAnyPermission(user, item.permissions);
    }
    if (item.adminOnly) {
      return isAdmin(user);
    }
    return true;
  });
};

// Constantes para las acciones comunes
export const ACTIONS = {
  CREATE_USER: [PERMISSIONS.MANAGE_USERS],
  EDIT_USER: [PERMISSIONS.MANAGE_USERS],
  DELETE_USER: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.DELETE],
  VIEW_USER_DETAILS: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.READ],
  
  CREATE_EMPLOYEE: [PERMISSIONS.MANAGE_EMPLOYEES],
  EDIT_EMPLOYEE: [PERMISSIONS.MANAGE_EMPLOYEES],
  DELETE_EMPLOYEE: [PERMISSIONS.MANAGE_EMPLOYEES, PERMISSIONS.DELETE],
  VIEW_EMPLOYEE_DETAILS: [PERMISSIONS.MANAGE_EMPLOYEES, PERMISSIONS.READ],
  
  CREATE_PAYROLL: [PERMISSIONS.MANAGE_PAYROLLS],
  EDIT_PAYROLL: [PERMISSIONS.MANAGE_PAYROLLS],
  DELETE_PAYROLL: [PERMISSIONS.MANAGE_PAYROLLS, PERMISSIONS.DELETE],
  APPROVE_PAYROLL: [PERMISSIONS.APPROVE_PAYROLLS],
  PROCESS_PAYMENT: [PERMISSIONS.PROCESS_PAYMENTS],
  
  VIEW_REPORTS: [PERMISSIONS.VIEW_REPORTS],
  EXPORT_REPORTS: [PERMISSIONS.EXPORT_DATA],
  
  MANAGE_COMPANY_SETTINGS: [PERMISSIONS.MANAGE_COMPANIES, PERMISSIONS.MANAGE_SETTINGS]
};

export default {
  PERMISSIONS,
  ROLES,
  ACTIONS,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getRoleInfo,
  getUserPermissions,
  isAdmin,
  isSuperAdmin,
  filterMenuByPermissions
}; 